// Dynamic imports for better code splitting

interface AdminStatsData {
  users: any[];
  conversations: any[];
  documents: any[];
  casos: any[];
  plans: any[];
  plansAdoption: any[];
  tokens: any[];
  online: any[];
  totals: {
    users: number;
    conversations: number;
    documents: number;
    casos: number;
    totalTokens: number;
  };
}

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('pt-BR').format(num);
};

const getPeriodLabel = (period: string): string => {
  const labels: Record<string, string> = {
    '24h': 'Últimas 24 Horas',
    '7d': 'Últimos 7 Dias',
    '15d': 'Últimos 15 Dias',
    '30d': 'Últimos 30 Dias',
    '60d': 'Últimos 60 Dias',
    '90d': 'Últimos 90 Dias',
  };
  return labels[period] || period;
};

export const exportStatisticsToPDF = async (
  data: AdminStatsData,
  period: string,
  chartRefs: {
    usersGrowth: HTMLElement | null;
    activity: HTMLElement | null;
    plansDistribution: HTMLElement | null;
    plansAdoption: HTMLElement | null;
    resourcesUsage: HTMLElement | null;
    onlineUsers: HTMLElement | null;
  }
) => {
  try {
    // Dynamic imports for code splitting
    const [html2canvas, { jsPDF }] = await Promise.all([
      import('html2canvas').then(m => m.default),
      import('jspdf')
    ]);

    // Create PDF in landscape mode
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;

    // Page 1: Cover with summary
    pdf.setFillColor(59, 130, 246); // Primary blue
    pdf.rect(0, 0, pageWidth, 60, 'F');

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(32);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Relatório Estatístico', margin, 30);

    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Período: ${getPeriodLabel(period)}`, margin, 45);

    const currentDate = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    pdf.setFontSize(12);
    pdf.text(`Gerado em: ${currentDate}`, margin, 53);

    // Summary section
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Resumo Executivo', margin, 80);

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    const summaryY = 95;
    const lineHeight = 12;

    const summaryData = [
      { label: 'Total de Usuários:', value: formatNumber(data.totals.users) },
      { label: 'Total de Conversas:', value: formatNumber(data.totals.conversations) },
      { label: 'Total de Documentos:', value: formatNumber(data.totals.documents) },
      { label: 'Total de Casos:', value: formatNumber(data.totals.casos) },
      { label: 'Total de Tokens:', value: formatNumber(data.totals.totalTokens) },
    ];

    summaryData.forEach((item, index) => {
      const y = summaryY + index * lineHeight;
      pdf.setFont('helvetica', 'bold');
      pdf.text(item.label, margin, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(item.value, margin + 70, y);
    });

    // Footer
    pdf.setFontSize(10);
    pdf.setTextColor(128, 128, 128);
    pdf.text('Justiçai - Inteligência Artificial Jurídica', pageWidth / 2, pageHeight - 10, {
      align: 'center',
    });

    // Chart pages configuration
    const charts = [
      {
        ref: chartRefs.usersGrowth,
        title: 'Crescimento de Usuários',
        description: 'Novos cadastros no período',
      },
      {
        ref: chartRefs.activity,
        title: 'Atividade da Plataforma',
        description: 'Conversas, documentos e casos criados',
      },
      {
        ref: chartRefs.plansDistribution,
        title: 'Distribuição de Planos',
        description: 'Usuários por tipo de plano',
      },
      {
        ref: chartRefs.plansAdoption,
        title: 'Adesão de Planos',
        description: 'Novas assinaturas no período',
      },
      {
        ref: chartRefs.resourcesUsage,
        title: 'Consumo de Recursos',
        description: 'Tokens utilizados (input/output)',
      },
      {
        ref: chartRefs.onlineUsers,
        title: 'Usuários Ativos',
        description: 'Atividade ao longo do período',
      },
    ];

    // Capture and add each chart
    for (let i = 0; i < charts.length; i++) {
      const chart = charts[i];
      if (!chart.ref) continue;

      pdf.addPage();

      // Chart title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(chart.title, margin, 20);

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(128, 128, 128);
      pdf.text(chart.description, margin, 28);

      // Capture chart as image
      const canvas = await html2canvas(chart.ref, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Center the image vertically
      const yPosition = (pageHeight - imgHeight - 40) / 2 + 40;

      pdf.addImage(imgData, 'JPEG', margin, yPosition, imgWidth, imgHeight);

      // Footer with period and date
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(
        `Período: ${getPeriodLabel(period)} | ${currentDate}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `estatisticas-${period}-${timestamp}.pdf`;
    pdf.save(filename);

    return true;
  } catch (error) {
    console.error('Erro ao exportar PDF:', error);
    throw error;
  }
};
